import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "./ui/badge";
import { cn } from "@/lib/utils";

interface FileMetadata {
  size?: number;
  dimensions?: { width: number; height: number };
  mimeType?: string;
}

interface FileMetadataProps {
  url: string;
  className?: string;
  size?: number;
}

export const FileMetadata: React.FC<FileMetadataProps> = ({ url, className, size }) => {
  const { t } = useTranslation();
  const [metadata, setMetadata] = useState<FileMetadata>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();

    const fetchMetadata = async () => {
      if (!url) return;
      setIsLoading(true);
      setError(null);

      try {
        const fileExtension = url.split(".").pop()?.toLowerCase();
        const isImage = /^(jpg|jpeg|png|gif|webp|svg|avif)$/i.test(fileExtension || "");
        const isVideo = /^(mp4|webm|ogg|mov)$/i.test(fileExtension || "");
        const isAudio = /^(mp3|wav|ogg|m4a)$/i.test(fileExtension || "");

        // Set initial mime type based on extension
        if (mounted) {
          setMetadata((prev) => ({
            ...prev,
            mimeType: isImage
              ? `image/${fileExtension}`
              : isVideo
              ? `video/${fileExtension}`
              : isAudio
              ? `audio/${fileExtension}`
              : undefined,
          }));
        }

        // Get dimensions if it's an image (without waiting for size)
        if (isImage) {
          const img = new Image();
          img.src = url;
          img.onload = () => {
            if (mounted) {
              setMetadata((prev) => ({
                ...prev,
                dimensions: { width: img.width, height: img.height },
              }));
            }
          };
        }

        // Try to get file size with a timeout
        try {
          const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 5000));

          const fetchPromise = fetch(url, {
            signal: controller.signal,
            credentials: "omit",
          });

          const response = await Promise.race([fetchPromise, timeoutPromise]);
          const blob = await (response as any)?.blob();

          if (mounted) {
            setMetadata((prev) => ({
              ...prev,
              size: blob.size,
            }));
          }
        } catch (sizeError) {
          // Silently fail for size fetch - we'll just not show the size
          console.warn("Could not fetch file size:", sizeError);
        }
      } catch (error) {
        console.error("Error fetching file metadata:", error);
        // Only set error if we have no metadata at all
        if (mounted && !metadata.dimensions && !metadata.size && !metadata.mimeType) {
          setError("Failed to fetch file metadata");
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMetadata();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [url]);

  if (isLoading) {
    return null;
  }

  // Only show error if we have no metadata at all
  if (error && !metadata.dimensions && !metadata.size && !metadata.mimeType) {
    return <div className={className}>{error}</div>;
  }

  return (
    <div className={cn(className, "flex flex-wrap gap-2")}>
      {metadata.size && (
        <Badge className="text-xs bg-primary/80">
          <span className="font-bold">{t("common.size")}</span>: {size}MB
        </Badge>
      )}
      {metadata.dimensions && (
        <Badge className="text-xs bg-primary/80">
          {metadata.dimensions.width}x{metadata.dimensions.height}
        </Badge>
      )}
      {metadata.mimeType && <Badge className="text-xs bg-primary/80">{metadata.mimeType}</Badge>}
    </div>
  );
};
